import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { SecurityPulseGate } from "../components/SecurityPulseGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthShell = ({ title, kicker, children, footer }) => (
  <div className="min-h-screen bg-[#070707] text-[#F5F5F0] grid lg:grid-cols-2">
    <Toaster position="bottom-right" theme="dark" richColors />
    <div className="hidden lg:block relative overflow-hidden border-r border-white/10">
      <img src="https://images.unsplash.com/photo-1781522880528-23804c610b9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Vinci construction" className="absolute inset-0 w-full h-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/50 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo-mark.png" alt="Vinci" className="h-8 w-auto" />
          <span className="font-serif text-xl">Vinci Inc.</span>
        </Link>
        <div>
          <span className="overline text-[#B87333]">Veni · Vidi · Vici</span>
          <p className="font-serif text-4xl leading-tight mt-4 max-w-sm">Building the world in bulk.</p>
        </div>
      </div>
    </div>
    <div className="flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <span className="overline text-[#B87333]">{kicker}</span>
        <h1 className="font-serif text-4xl md:text-5xl mt-4 mb-10">{title}</h1>
        {children}
        <div className="mt-8 font-body text-sm text-[#8A8A82]">{footer}</div>
      </motion.div>
    </div>
  </div>
);

const Field = ({ label, testid, ...props }) => (
  <label className="block group mb-6">
    <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">{label}</span>
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors"
    />
  </label>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [suspended, setSuspended] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuspended("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      const nextParam = new URLSearchParams(loc.search).get("next");
      const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
      const dest = safeNext || loc.state?.from || (u.role === "admin" ? "/admin/command" : u.role === "director" && u.director_status && u.director_status !== "active" ? "/director/apply" : "/account");
      nav(dest);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "pioneer_access_suspended") { setSuspended(detail.message); return; }
      toast.error(formatApiErrorDetail(detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell kicker="/ Client & Trade Desk" title="Sign in" footer={<>New to Vinci? <Link to="/register" className="text-[#B87333] hover:underline">Create an account</Link></>}>
      <form onSubmit={submit} data-testid="login-form">
        {suspended && (
          <div data-testid="login-access-suspended" role="alert" className="mb-5 border border-[#C9A84C]/50 bg-[#C9A84C]/10 rounded-sm px-4 py-3">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A84C] mb-1">Access suspended</div>
            <p className="text-sm text-[#F5F5F0]">{suspended}</p>
          </div>
        )}
        <Field label="Email" testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label="Password" testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <div className="mt-2 mb-4 text-right">
          <Link
            to="/forgot-password"
            data-testid="login-forgot-password-link"
            className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#B87333] transition-colors"
          >
            Forgot password?
          </Link>
        </div>
        <button data-testid="login-submit" disabled={loading} className="mt-2 w-full rounded-full bg-[#F5F5F0] text-black py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}


export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Check your inbox — a reset link has been sent if the address is registered.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      kicker="/ Password Recovery"
      title="Reset your password"
      footer={<>Remembered it? <Link to="/login" className="text-[#B87333] hover:underline">Back to sign in</Link></>}
    >
      {sent ? (
        <div data-testid="forgot-password-sent" className="border border-[#2FB999]/40 bg-[#2FB999]/[0.06] rounded-sm p-6">
          <div className="font-serif text-2xl text-[#F5F5F0] mb-3">Check your inbox</div>
          <p className="font-body text-sm text-[#C9C9C2] leading-relaxed">
            If <b className="text-[#F5F5F0]">{email}</b> is registered on Vinci, a password-reset link is on its way. The link expires in 60 minutes and can only be used once.
          </p>
          <p className="font-body text-xs text-[#8A8A82] mt-4">
            Didn't receive it? Check your spam folder, or <button type="button" data-testid="forgot-password-resend" onClick={() => setSent(false)} className="text-[#B87333] hover:underline">try again</button>.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form">
          <p className="font-body text-sm text-[#8A8A82] mb-6 leading-relaxed">
            Enter the email associated with your Vinci account and we'll email you a secure link to choose a new password.
          </p>
          <Field label="Email" testid="forgot-password-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="forgot-password-submit" disabled={loading} className="mt-2 w-full rounded-full bg-[#F5F5F0] text-black py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}


export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => { if (!token) setMissing(true); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login"), 1600);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      kicker="/ Password Recovery"
      title="Choose a new password"
      footer={<>Back to <Link to="/login" className="text-[#B87333] hover:underline">sign in</Link></>}
    >
      {missing ? (
        <div data-testid="reset-password-missing-token" className="border border-[#C0392B]/40 bg-[#C0392B]/[0.05] rounded-sm p-6">
          <div className="font-serif text-xl text-[#F5F5F0] mb-2">Reset link is missing</div>
          <p className="font-body text-sm text-[#C9C9C2]">
            This page requires a valid one-time link.{" "}
            <Link to="/forgot-password" className="text-[#B87333] hover:underline">Request a new one</Link>.
          </p>
        </div>
      ) : done ? (
        <div data-testid="reset-password-done" className="border border-[#2FB999]/40 bg-[#2FB999]/[0.06] rounded-sm p-6">
          <div className="font-serif text-2xl text-[#F5F5F0] mb-2">Password updated</div>
          <p className="font-body text-sm text-[#C9C9C2]">Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="reset-password-form">
          <p className="font-body text-sm text-[#8A8A82] mb-6 leading-relaxed">
            Choose a strong new password. Once saved, you'll be signed out of any other sessions.
          </p>
          <Field label="New password (min 6 chars)" testid="reset-password-new" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} />
          <Field label="Confirm new password" testid="reset-password-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <button data-testid="reset-password-submit" disabled={loading} className="mt-2 w-full rounded-full bg-[#F5F5F0] text-black py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [pulseOpen, setPulseOpen] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // Security Pulse gate — verify email ownership before creating account.
    setPulseOpen(true);
  };

  const finishRegister = async (otp_code) => {
    setPulseOpen(false);
    setLoading(true);
    try {
      await register({ ...form, otp_code });
      toast.success("Account created. Welcome to Vinci.");
      nav("/account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell kicker="/ Open an Account" title="Create account" footer={<>Already have an account? <Link to="/login" className="text-[#B87333] hover:underline">Sign in</Link></>}>
      <form onSubmit={submit} data-testid="register-form">
        <Field label="Full name" testid="register-name" name="name" required value={form.name} onChange={onChange} />
        <Field label="Email" testid="register-email" name="email" type="email" required value={form.email} onChange={onChange} />
        <Field label="Company" testid="register-company" name="company" value={form.company} onChange={onChange} />
        <Field label="Password (min 6 chars)" testid="register-password" name="password" type="password" required minLength={6} value={form.password} onChange={onChange} />
        <button data-testid="register-submit" disabled={loading} className="mt-2 w-full rounded-full bg-[#F5F5F0] text-black py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <SecurityPulseGate
        open={pulseOpen}
        email={form.email}
        purpose="signup"
        displayName={form.name}
        onClose={() => setPulseOpen(false)}
        onVerified={finishRegister}
      />
    </AuthShell>
  );
}
